import React, { useEffect, useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { MapPin, Menu, User, LogOut, Settings, Home, Navigation } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';

 const AppContainer = styled.div`
  min-height: 100vh;
  background: #0f0f0f;
  color: #ffffff;
  padding-top: 80px; /* Add padding to account for fixed navbar height */
  
  @media (max-width: 768px) {
    padding-top: 70px; /* Reduced padding on mobile */
  }
  
  @media (max-width: 480px) {
    padding-top: 68px; /* Even less padding on small mobile */
  }
`;

const LocationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    opacity: 0.8;
  }
  
  @media (max-width: 768px) {
    gap: 4px;
    flex-direction: column;
    align-items: flex-start;
  }
  
  @media (max-width: 480px) {
    gap: 2px;
  }
`;

const LocationText = styled.p`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
  
  @media (max-width: 768px) {
    font-size: 12px;
    gap: 4px;
  }
  
  @media (max-width: 480px) {
    font-size: 11px;
    gap: 3px;
  }
`;

const LocationButton = styled.button`
  background: rgba(244, 63, 96, 0.1);
  border: 1px solid rgba(244, 63, 96, 0.3);
  border-radius: 6px;
  padding: 4px 8px;
  color: #F43F60;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &:hover {
    background: rgba(244, 63, 96, 0.2);
    border-color: rgba(244, 63, 96, 0.5);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    padding: 3px 6px;
    font-size: 11px;
  }
`;

const UserMenu = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  
  @media (max-width: 768px) {
    gap: 8px;
  }
  
  @media (max-width: 480px) {
    gap: 6px;
  }
`;

const UserAvatar = styled.div`
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #F43F60, #E03A56);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
  
  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
    font-size: 12px;
  }
  
  @media (max-width: 480px) {
    width: 28px;
    height: 28px;
    font-size: 11px;
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const UserName = styled.span`
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
`;

const UserEmail = styled.span`
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  line-height: 1.2;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: #1a1a1a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 8px 0;
  min-width: 200px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  z-index: 1000;
  margin-top: 8px;
  
  @media (max-width: 768px) {
    position: fixed;
    top: 60px;
    left: 0;
    right: 0;
    margin: 0 16px;
    min-width: auto;
    border-radius: 8px;
  }
  
  @media (max-width: 480px) {
    top: 55px;
    margin: 0 12px;
    border-radius: 6px;
  }
`;

const DropdownItem = styled(Link)`
  display: flex !important;
  align-items: center !important;
  gap: 12px !important;
  padding: 12px 16px !important;
  color: #ffffff;
  text-decoration: none;
  font-size: 16px !important;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #F43F60;
  }
  
  @media (max-width: 768px) {
    padding: 16px 20px;
    font-size: 16px;
  }
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  background: none;
  border: none;
  color: #F43F60;
  font-size: 18px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(244, 63, 96, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 16px 20px;
    font-size: 16px;
  }
`;

const MobileZipCodeContainer = styled.div`
  padding: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 8px;
  background: rgba(255, 255, 255, 0.02);
`;

const MobileLocationInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  outline: none;
  margin-bottom: 8px;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    border-color: #F43F60;
    box-shadow: 0 0 0 2px rgba(244, 63, 96, 0.2);
  }
  
  @media (max-width: 480px) {
    padding: 8px 10px;
    font-size: 13px;
  }
`;

const ZipCodeButtonContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  
  @media (max-width: 480px) {
    gap: 6px;
  }
`;

const ZipCodeButton = styled.button`
  background: rgba(244, 63, 96, 0.1);
  border: 1px solid rgba(244, 63, 96, 0.3);
  border-radius: 6px;
  padding: 8px 12px;
  color: #F43F60;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  
  &:hover {
    background: rgba(244, 63, 96, 0.2);
    border-color: rgba(244, 63, 96, 0.5);
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
  
  @media (max-width: 480px) {
    padding: 10px 8px;
    font-size: 12px;
  }
`;

const DesktopLocationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const DesktopZipCodeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 5px;
  min-width: 280px;
`;

const DesktopZipCodeInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  color: #ffffff;
  font-size: 14px;
  padding: 0.25rem 0.5rem;
  outline: none;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    outline: none;
  }
`;

const DesktopButtonContainer = styled.div`
  display: flex;
  gap: 0.25rem;
`;

const DesktopZipCodeButton = styled.button`
  background: rgba(244, 63, 96, 0.1);
  border: 1px solid rgba(244, 63, 96, 0.3);
  border-radius: 6px;
  padding: 0.25rem 0.5rem;
  color: #F43F60;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  white-space: nowrap;
  
  &:hover {
    background: rgba(244, 63, 96, 0.2);
    border-color: rgba(244, 63, 96, 0.5);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const MobileLocationMenuItem = styled.li`
  @media (min-width: 769px) {
    display: none;
  }
  
  @media (max-width: 768px) {
    display: block;
    width: 100%;
    list-style: none;
  }
`;
 
const PublicLayout = ({ children }) => {
   const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [currentCity, setCurrentCity] = useState('Unknown');
  const [currentPostalCode, setCurrentPostalCode] = useState('');
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const { isAuthenticated, isDealerAuthenticated, isUserAuthenticated, isDealer, isUser, user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Check for existing zip code in session storage
    const savedZipCode = sessionStorage.getItem("postalCode");
    const savedCity = sessionStorage.getItem("city");
    
    if (savedZipCode && savedCity) {
      setCurrentPostalCode(savedZipCode);
      setCurrentCity(savedCity);
    } else {
      // If no saved location, get current location
      getCurrentLocation();
    }
  }, []);
  const getAddressFromPostalCode = async (postalCode, countryCode = "us") => {
    if(!postalCode) {
      return await getCurrentLocation();
    }
    const url = `https://nominatim.openstreetmap.org/search?postalcode=${encodeURIComponent(postalCode)}&country=${encodeURIComponent(countryCode)}&format=json&addressdetails=1`;
    
    const res = await fetch(url, {
      headers: { 'User-Agent': 'PostalCodeLookup/1.0' } // required by Nominatim
    });
    const data = await res.json();
    console.log(data)
    if (data.length > 0) {
      if(data[0].address){
        const state = iso3166ToUSState(data[0].address["ISO3166-2-lvl4"]);
        sessionStorage.setItem("state", state?.name);
        sessionStorage.setItem("stateCode", state?.abbr);
        sessionStorage.setItem("postalCode", data[0].address.postcode);
        sessionStorage.setItem("city", data[0].address.city);
        sessionStorage.setItem("country", data[0].address.country);
        sessionStorage.setItem("countryCode", data[0].address.country_code);
        sessionStorage.setItem("latitude", data[0].lat);
        sessionStorage.setItem("longitude", data[0].lon);
        setCurrentCity(data[0].address.city);
        return data[0].address.city || 
        data[0].address.town || 
        data[0].address.village || 
        data[0].address.county ||
        data[0].address.state;; // full address
      }
 
    }
    return null;
  }
  const usStates = {
    "AL": { abbr: "AL", name: "Alabama" },
    "AK": { abbr: "AK", name: "Alaska" },
    "AZ": { abbr: "AZ", name: "Arizona" },
    "AR": { abbr: "AR", name: "Arkansas" },
    "CA": { abbr: "CA", name: "California" },
    "CO": { abbr: "CO", name: "Colorado" },
    "CT": { abbr: "CT", name: "Connecticut" },
    "DE": { abbr: "DE", name: "Delaware" },
    "DC": { abbr: "DC", name: "District of Columbia" },
    "FL": { abbr: "FL", name: "Florida" },
    "GA": { abbr: "GA", name: "Georgia" },
    "HI": { abbr: "HI", name: "Hawaii" },
    "ID": { abbr: "ID", name: "Idaho" },
    "IL": { abbr: "IL", name: "Illinois" },
    "IN": { abbr: "IN", name: "Indiana" },
    "IA": { abbr: "IA", name: "Iowa" },
    "KS": { abbr: "KS", name: "Kansas" },
    "KY": { abbr: "KY", name: "Kentucky" },
    "LA": { abbr: "LA", name: "Louisiana" },
    "ME": { abbr: "ME", name: "Maine" },
    "MD": { abbr: "MD", name: "Maryland" },
    "MA": { abbr: "MA", name: "Massachusetts" },
    "MI": { abbr: "MI", name: "Michigan" },
    "MN": { abbr: "MN", name: "Minnesota" },
    "MS": { abbr: "MS", name: "Mississippi" },
    "MO": { abbr: "MO", name: "Missouri" },
    "MT": { abbr: "MT", name: "Montana" },
    "NE": { abbr: "NE", name: "Nebraska" },
    "NV": { abbr: "NV", name: "Nevada" },
    "NH": { abbr: "NH", name: "New Hampshire" },
    "NJ": { abbr: "NJ", name: "New Jersey" },
    "NM": { abbr: "NM", name: "New Mexico" },
    "NY": { abbr: "NY", name: "New York" },
    "NC": { abbr: "NC", name: "North Carolina" },
    "ND": { abbr: "ND", name: "North Dakota" },
    "OH": { abbr: "OH", name: "Ohio" },
    "OK": { abbr: "OK", name: "Oklahoma" },
    "OR": { abbr: "OR", name: "Oregon" },
    "PA": { abbr: "PA", name: "Pennsylvania" },
    "RI": { abbr: "RI", name: "Rhode Island" },
    "SC": { abbr: "SC", name: "South Carolina" },
    "SD": { abbr: "SD", name: "South Dakota" },
    "TN": { abbr: "TN", name: "Tennessee" },
    "TX": { abbr: "TX", name: "Texas" },
    "UT": { abbr: "UT", name: "Utah" },
    "VT": { abbr: "VT", name: "Vermont" },
    "VA": { abbr: "VA", name: "Virginia" },
    "WA": { abbr: "WA", name: "Washington" },
    "WV": { abbr: "WV", name: "West Virginia" },
    "WI": { abbr: "WI", name: "Wisconsin" },
    "WY": { abbr: "WY", name: "Wyoming" }
  };

  function iso3166ToUSState(codeStr) {
    if (!codeStr) return null;
    const normalized = codeStr.replace(/[:_]/g, "-").toUpperCase();
    const parts = normalized.split("-");
    if (parts[0] !== "US" || parts.length < 2) return null;
  
    const stateCode = parts[1];
    return usStates[stateCode] || null;
  }
  
  // Get city name from coordinates using reverse geocoding
  const getCityFromCoordinates = async (latitude, longitude) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=30&addressdetails=1`
      );
      const data = await response.json();
      
      if (data.address) {
        const state = iso3166ToUSState(data.address["ISO3166-2-lvl4"]);
        sessionStorage.setItem("state", state?.name);
        sessionStorage.setItem("stateCode", state?.abbr);
        sessionStorage.setItem("postalCode", data.address.postcode);
        sessionStorage.setItem("city", data.address.city);
        sessionStorage.setItem("country", data.address.country);
        sessionStorage.setItem("countryCode", data.address.country_code);
        sessionStorage.setItem("latitude", latitude);
        sessionStorage.setItem("longitude", longitude);
        // Try to get city name from various address components
        const city = data.address.city || 
                    data.address.town || 
                    data.address.village || 
                    data.address.county ||
                    data.address.state;
        
        const postalCode = data.address.postcode;
        if (city) {
          return {city,postalCode};
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error getting city from coordinates:', error);
      return null;
    }
  };

  // Get current location and update city
  const getCurrentLocation = async () => {
    setIsGettingLocation(true);
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          const {city,postalCode} = await getCityFromCoordinates(latitude, longitude);
          
          if (city) {
            setCurrentCity(city);
          }
          if (postalCode) {
            setCurrentPostalCode(postalCode);
          }
          setIsGettingLocation(false);
        },
        (error) => {
          console.error('Error getting location:', error);
          setIsGettingLocation(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        }
      );
    } else {
      console.log('Geolocation is not supported by this browser.');
      setIsGettingLocation(false);
    }
  };

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
    navigate('/');
  };

  const getUserInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleZipCodeChange = (e) => {
    const postalCode = e.target.value;
    setCurrentPostalCode(postalCode);
  };

  const handleSetZipCode = () => {
    if (currentPostalCode.trim()) {
      getAddressFromPostalCode(currentPostalCode.trim());
    }
  };

  const handleGetCurrentLocation = () => {
    getCurrentLocation();
  };

  
  return (
    <>
         <header className="custom-header-area">
        <div className="custom-containers">
          <div className="custom-row">
            <div className="custom-col-12">
              <div className="custom-navigation">
                <nav className="custom-navbar custom-navbar-expand-xl">
                  <div className="custom-container-fluid custom-p-0">
                    <a className="custom-navbar-brand" href="/">
                      <img className="custom-img-fluid" src={'/logo.svg'} alt="logo" loading="lazy" />
                    </a>
                    {/* Desktop location - hidden on mobile */}
                    <DesktopLocationContainer className="custom-location">
                      <LocationContainer onClick={getCurrentLocation}>
                        <LocationText>
                          <MapPin size={16} /> 
                          {currentCity}
                        </LocationText>
                      </LocationContainer>
                      <DesktopZipCodeContainer>
                        <DesktopZipCodeInput 
                          type="text" 
                          placeholder="Enter Zip Code" 
                          value={currentPostalCode} 
                          onChange={(e) => handleZipCodeChange(e)}
                        />
                        <DesktopButtonContainer>
                          <DesktopZipCodeButton 
                            onClick={handleSetZipCode}
                            disabled={!currentPostalCode.trim()}
                            title="Set zip code"
                          >
                            Set
                          </DesktopZipCodeButton>
                          <DesktopZipCodeButton 
                            onClick={handleGetCurrentLocation}
                            disabled={isGettingLocation}
                            title="Get current location"
                          >
                            <Navigation size={10} />
                            {isGettingLocation ? '...' : 'GPS'}
                          </DesktopZipCodeButton>
                        </DesktopButtonContainer>
                      </DesktopZipCodeContainer>
                    </DesktopLocationContainer>
                   
                    {/* Overlay for mobile menu */}
                    {isMenuOpen && <div className="custom-menu-overlay" onClick={() => setIsMenuOpen(false)}></div>}
                    <div
                      className={`custom-collapse custom-navbar-collapse${isMenuOpen ? ' custom-show' : ''}`}
                      id="custom-navbarSupportedContent"
                      style={{ display: isMenuOpen ? 'flex' : '', flexDirection: isMenuOpen ? 'column' : undefined }}
                    >
                      <ul id="custom-main-menu" className="custom-navbar-nav custom-me-auto custom-sm custom-sm-simple">
                        {/* Mobile zip code controls - shown as first menu item */}
                        <MobileLocationMenuItem>
                          <MobileZipCodeContainer>
                            <div style={{ marginBottom: '8px' }}>
                              <LocationContainer onClick={getCurrentLocation}>
                                <LocationText>
                                  <MapPin size={16} /> 
                                  {currentCity}
                                </LocationText>
                              </LocationContainer>
                            </div>
                            <MobileLocationInput 
                              type="text" 
                              placeholder="Enter Zip Code" 
                              value={currentPostalCode} 
                              onChange={(e) => handleZipCodeChange(e)}
                            />
                            <ZipCodeButtonContainer>
                              <ZipCodeButton 
                                onClick={handleSetZipCode}
                                disabled={!currentPostalCode.trim()}
                                title="Set zip code"
                              >
                                Set Zip Code
                              </ZipCodeButton>
                              <ZipCodeButton 
                                onClick={handleGetCurrentLocation}
                                disabled={isGettingLocation}
                                title="Get current location"
                              >
                                <Navigation size={12} />
                                {isGettingLocation ? 'Getting...' : 'Use GPS'}
                              </ZipCodeButton>
                            </ZipCodeButtonContainer>
                          </MobileZipCodeContainer>
                        </MobileLocationMenuItem>
                        {isUserAuthenticated && user ? (
                          <li>
                            <UserMenu>
                              <UserInfo>
                                <UserName>{user.userInfo?.name || 'User'}</UserName>
                                <UserEmail>{user.email}</UserEmail>
                              </UserInfo>
                              <UserAvatar 
                                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                                title={user.userInfo?.name || 'User'}
                              >
                                {getUserInitials(user.userInfo?.name)}
                              </UserAvatar>
                              {isUserMenuOpen && (
                                <DropdownMenu>
                                  <DropdownItem to="/user/dashboard" onClick={() => setIsUserMenuOpen(false)}>
                                    <Home size={16} />
                                    Dashboard
                                  </DropdownItem>
                                  {/* <DropdownItem to="/user/profile" onClick={() => setIsUserMenuOpen(false)}>
                                    <User size={16} />
                                    Profile
                                  </DropdownItem>
                                  <DropdownItem to="/user/settings" onClick={() => setIsUserMenuOpen(false)}>
                                    <Settings size={16} />
                                    Settings
                                  </DropdownItem> */}
                                  <LogoutButton onClick={handleLogout}>
                                    <LogOut size={16} />
                                    Logout
                                  </LogoutButton>
                                </DropdownMenu>
                              )}
                            </UserMenu>
                          </li>
                        ) : (
                          <>
                        <li><Link to="/user/login" onClick={() => setIsMenuOpen(false)}>Sign up / Log in</Link></li>
                        <li><Link to="/login" className="custom-dealerslog" onClick={() => {
                          setIsMenuOpen(false)
                        }}>Dealers Log in</Link></li>
                          </>
                        )}
                      </ul>
                    </div>
                    <button
                      className="custom-navbar-toggler"
                      type="button"
                      aria-controls="custom-navbarSupportedContent"
                      aria-expanded={isMenuOpen}
                      aria-label="Toggle navigation"
                      onClick={() => setIsMenuOpen((open) => !open)}
                    >
                        <Menu size={25} />
                    </button>
                  </div>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </header>

      <AppContainer>
        {children}
      </AppContainer>
    </>
  );
};

export default PublicLayout; 